import React, { useState, useEffect, useRef, useCallback } from 'react';
import MediumButton from '../ui/MediumButton';
import { EditUndoIcon, ClearIcon, BookmarkOutlineIcon, WandIcon, ChevronDownIcon } from '../../utils/icons';
import SaveDescriptionModal from '../modals/SaveDescriptionModal';
import SavedDescriptionsModal from '../modals/SavedDescriptionsModal';
import colors from '../../utils/colors';
import typography from '../../utils/typography';

const PlusIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className="w-4 h-4"
  >
    <path
      d="M8 2C7.52661 2 7.14286 2.38376 7.14286 2.85714V7.14286L2.85714 7.14286C2.38376 7.14286 2 7.52661 2 8C2 8.47339 2.38376 8.85714 2.85714 8.85714H7.14286V13.1429C7.14286 13.6162 7.52661 14 8 14C8.47339 14 8.85714 13.6162 8.85714 13.1429V8.85714H13.1429C13.6162 8.85714 14 8.47339 14 8C14 7.52661 13.6162 7.14286 13.1429 7.14286L8.85714 7.14286V2.85714C8.85714 2.38376 8.47339 2 8 2Z"
      fill="#FAF7F5"
    />
  </svg>
);

const WhiteSpinner = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" className="w-4 h-4">
    <circle cx="8" cy="8" r="6" fill="none" stroke="#FFFFFF" strokeWidth="2" strokeLinecap="round" strokeDasharray="18 12">
      <animateTransform attributeName="transform" type="rotate" from="0 8 8" to="360 8 8" dur="1s" repeatCount="indefinite"></animateTransform>
    </circle>
  </svg>
);

const GenreTag = ({ genre, onRemove, isAddButton = false, onClick, isLoading = false }) => {
  if (isAddButton) {
    return (
      <MediumButton
        variant="secondary"
        text={genre}
        leftIcon={isLoading ? <WhiteSpinner /> : <PlusIcon />}
        onClick={isLoading ? undefined : onClick}
        className={isLoading ? 'cursor-not-allowed opacity-50' : ''}
      />
    );
  }

  return (
    <MediumButton
      variant="secondary"
      text={genre}
      leftIcon={<PlusIcon />}
      className="border border-white/10"
    />
  );
};

const StyleDescription = ({
  description = "",
  onDescriptionChange,
  genres = [],
  onGenreAdd,
  onGenreRemove,
  onUndo,
  onClear,
  onSave,
  isSaved = false,
  hasSavedDescriptions = false,
  savedDescriptions = [],
  onSavedDescriptionSelect,
  defaultCollapsed = false,
  collapsed,
  onCollapsedChange
}) => {
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showSavedModal, setShowSavedModal] = useState(false);
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [componentHeight, setComponentHeight] = useState(null);
  const [internalCollapsed, setInternalCollapsed] = useState(defaultCollapsed);

  // Use controlled state if provided, otherwise use internal state
  const isCollapsed = collapsed !== undefined ? collapsed : internalCollapsed;
  const setIsCollapsed = onCollapsedChange || setInternalCollapsed;
  const componentRef = useRef(null);
  const [suggestionGenres, setSuggestionGenres] = useState([
    "hip hop", "rnb", "pop", "dance", "country", "rock", "jazz"
  ]);
  const availableGenres = [
    "hip hop", "rnb", "pop", "dance", "country", "rock", "jazz", "classical", 
    "electronic", "folk", "blues", "reggae", "punk", "metal", "indie"
  ];

  const hasContent = description || genres.length > 0;


  // Calculate dynamic height based on description content
  const calculateHeight = () => {
    const baseHeight = 350; // Minimum height
    const maxHeight = 350;
    const lineHeight = 24; // Approximate line height
    const minLinesForGrowth = 3; // Start growing after 3 lines
    
    if (!description) return baseHeight;
    
    // Calculate both explicit line breaks and text wrapping
    const explicitLines = description.split('\n').length;
    
    // Estimate wrapped lines based on character count
    // Assuming approximately 50 characters per line for song description
    const avgCharsPerLine = 50;
    const totalLines = description.split('\n').reduce((total, line) => {
      const wrappedLines = Math.max(1, Math.ceil(line.length / avgCharsPerLine));
      return total + wrappedLines;
    }, 0);
    
    // Use the larger of explicit lines or total estimated lines
    const effectiveLines = Math.max(explicitLines, totalLines);
    
    if (effectiveLines <= minLinesForGrowth) return baseHeight;
    
    const additionalLines = effectiveLines - minLinesForGrowth;
    const calculatedHeight = baseHeight + (additionalLines * lineHeight);
    
    return Math.min(calculatedHeight, maxHeight);
  };
  
  const dynamicHeight = calculateHeight();

  const handleSaveDescription = (title) => {
    onSave?.(title);
    setShowSaveModal(false);
  };

  const handleSelectSavedDescription = (savedItem) => {
    onSavedDescriptionSelect?.(savedItem);
    setShowSavedModal(false);
  };

  const handleGenreClick = async (genre) => {
    // Add genre to description text
    const currentText = description || "";
    const newText = currentText ? `${currentText}, ${genre}` : genre;
    onDescriptionChange?.(newText);

    // Set loading state for all suggestions
    setIsLoadingSuggestions(true);

    try {
      // Generate 7 related genres, instruments, and vocal styles using Gemini
      const apiKey = "AIzaSyBUK6J5FjHta2nM0ZDCzuXc3HNURA8eXTU";
      const apiUrl = `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.0-flash:generateContent?key=${apiKey}`;
      
      const prompt = `Given the music genre "${genre}", suggest exactly 7 related musical elements that would complement it well. Include a mix of:
- Related sub-genres or genre variations
- Relevant instrument styles (e.g., "electric guitar", "acoustic piano", "synthesizer", "live drums")
- Vocal styles (e.g., "female vocals", "male vocals", "harmonies", "rap vocals", "falsetto")
- Production styles (e.g., "reverb-heavy", "distorted", "clean", "lo-fi")

Return only the suggestions separated by commas, no explanation.

Examples:
- "rock" → "electric guitar, power chords, male vocals, live drums, distorted, alternative rock, punk rock"
- "jazz" → "saxophone, piano, female vocals, swing, bebop, smooth jazz, upright bass"
- "electronic" → "synthesizer, drum machine, vocoder, techno, house, ambient, reverb-heavy"`;
      
      const payload = {
        contents: [{ role: "user", parts: [{ text: prompt }] }],
        generationConfig: {
          maxOutputTokens: 150,
          temperature: 0.7,
          responseMimeType: "text/plain"
        }
      };

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const result = await response.json();
      
      if (result.candidates && result.candidates[0].content.parts.length > 0) {
        const suggestionsText = result.candidates[0].content.parts[0].text.trim();
        const newSuggestions = suggestionsText
          .split(',')
          .map(s => s.trim().toLowerCase())
          .filter(s => s && !genres.includes(s))
          .slice(0, 7);
        
        if (newSuggestions.length > 0) {
          setSuggestionGenres(newSuggestions);
        } else {
          // Fallback if no valid suggestions returned
          generateFallbackGenres(genre);
        }
      } else {
        generateFallbackGenres(genre);
      }
    } catch (error) {
      console.error("Error generating related suggestions:", error);
      generateFallbackGenres(genre);
    } finally {
      setIsLoadingSuggestions(false);
    }
  };

  const generateFallbackGenres = (excludeGenre) => {
    const fallbackGenres = availableGenres.filter(g => 
      !genres.includes(g) && g !== excludeGenre
    );
    
    // Shuffle and take 7 random genres
    const shuffled = fallbackGenres.sort(() => 0.5 - Math.random());
    setSuggestionGenres(shuffled.slice(0, 7));
  };

  const handleMagicWandClick = async () => {
    if (!description) return;

    try {
      const apiKey = "AIzaSyBUK6J5FjHta2nM0ZDCzuXc3HNURA8eXTU";
      const apiUrl = `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.0-flash:generateContent?key=${apiKey}`;
      
      const prompt = `Transform this basic style description into a more vivid, descriptive version that captures the vibe and mood of the musical style. Keep it concise but evocative. 

Original: "${description}"

Transform this into a more atmospheric description that describes how the style feels, its energy, mood, and vibe. Examples:
- "hip hop, rnb, female" → "smooth late-night R&B with silky female vocals over crisp hip hop beats"
- "electronic, dance, upbeat" → "pulsing electronic rhythms with euphoric dance energy"
- "rock, guitar, heavy" → "thunderous guitar-driven rock with crushing heavy riffs"

Return only the enhanced description, no explanation.`;
      
      const payload = {
        contents: [{ role: "user", parts: [{ text: prompt }] }],
        generationConfig: {
          maxOutputTokens: 100,
          temperature: 0.8
        }
      };

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const result = await response.json();
      
      if (result.candidates && result.candidates[0].content.parts.length > 0) {
        const enhancedDescription = result.candidates[0].content.parts[0].text.trim();
        onDescriptionChange?.(enhancedDescription);
      }
    } catch (error) {
      console.error("Error enhancing description:", error);
    }
  };

  // Drag functionality
  const handleDragStart = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
    const startY = e.clientY;
    const startHeight = componentRef.current ? componentRef.current.offsetHeight : dynamicHeight;

    const handleDragMove = (moveEvent) => {
      moveEvent.preventDefault();
      const deltaY = moveEvent.clientY - startY;
      const newHeight = Math.max(200, Math.min(600, startHeight + deltaY));
      setComponentHeight(newHeight);
      
      // Immediately update the component style for smoother dragging
      if (componentRef.current) {
        componentRef.current.style.height = `${newHeight}px`;
      }
    };

    const handleDragEnd = () => {
      setIsDragging(false);
      document.removeEventListener('mousemove', handleDragMove);
      document.removeEventListener('mouseup', handleDragEnd);
      
      // Re-enable transitions after dragging
      if (componentRef.current) {
        componentRef.current.style.transition = 'all 200ms ease-in-out';
      }
    };

    // Disable transitions during dragging for smoother experience
    if (componentRef.current) {
      componentRef.current.style.transition = 'none';
    }

    document.addEventListener('mousemove', handleDragMove);
    document.addEventListener('mouseup', handleDragEnd);
  }, [dynamicHeight]);

  // Touch drag functionality
  const handleTouchStart = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
    const startY = e.touches[0].clientY;
    const startHeight = componentRef.current ? componentRef.current.offsetHeight : dynamicHeight;

    const handleTouchMove = (moveEvent) => {
      moveEvent.preventDefault();
      const deltaY = moveEvent.touches[0].clientY - startY;
      const newHeight = Math.max(200, Math.min(600, startHeight + deltaY));
      setComponentHeight(newHeight);
      
      // Immediately update the component style for smoother dragging
      if (componentRef.current) {
        componentRef.current.style.height = `${newHeight}px`;
      }
    };

    const handleTouchEnd = () => {
      setIsDragging(false);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
      
      // Re-enable transitions after dragging
      if (componentRef.current) {
        componentRef.current.style.transition = 'all 200ms ease-in-out';
      }
    };

    // Disable transitions during dragging for smoother experience
    if (componentRef.current) {
      componentRef.current.style.transition = 'none';
    }

    document.addEventListener('touchmove', handleTouchMove);
    document.addEventListener('touchend', handleTouchEnd);
  }, [dynamicHeight]);

  const finalHeight = componentHeight !== null ? componentHeight : dynamicHeight;

  return (
    <>
      <style>{`
        .style-description-input::placeholder {
          color: ${colors.text.inactive};
        }
        .style-description-input::selection {
          background-color: ${colors.accent};
          color: ${colors.text.primary};
        }
      `}</style>
    <div 
      ref={componentRef}
      className="flex flex-col w-full gap-px relative" 
      style={{ height: isCollapsed ? '72px' : `${finalHeight}px` }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="flex flex-col w-full min-h-0 flex-grow overflow-visible rounded-xl" style={{ backgroundColor: colors.bg.fog.thin }}>
        {/* Header */}
        <div 
          className={`flex justify-between items-center px-4 min-h-[72px] flex-shrink-0 ${isCollapsed ? 'cursor-pointer' : ''}`}
          onClick={isCollapsed ? () => setIsCollapsed(!isCollapsed) : undefined}
        >
          <div className={`flex items-center gap-2 ${!isCollapsed ? 'cursor-pointer' : ''}`} onClick={!isCollapsed ? () => setIsCollapsed(!isCollapsed) : undefined}>
            <div
              className="flex items-center justify-center w-4 h-4 transition-transform duration-200"
              style={{
                transform: isCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)',
                color: colors.text.primary
              }}
            >
              <ChevronDownIcon />
            </div>
            <p className="text-white" style={typography.small.title}>
              Styles
            </p>
          </div>
          {!isCollapsed && hasContent && (
            <div className="flex items-center gap-2">
              <MediumButton variant="secondary" onClick={onUndo} tooltip="Undo" leftIcon={<EditUndoIcon />} />
              <MediumButton variant="secondary" onClick={onClear} tooltip="Clear" leftIcon={<ClearIcon />} />
              <MediumButton 
                variant="secondary"
                onClick={isSaved ? undefined : () => setShowSaveModal(true)}
                className={isSaved ? "opacity-30 cursor-not-allowed" : ""}
                tooltip="Save"
                leftIcon={<BookmarkOutlineIcon isStarred={isSaved} />}
              />
              <MediumButton 
                variant="aura"
                onClick={handleMagicWandClick}
                tooltip="Enhance"
                leftIcon={<WandIcon />}
              />
            </div>
          )}
        </div>
        
        {/* Description Text */}
        {!isCollapsed && (
          <div className="flex flex-col flex-grow px-4 relative min-h-0">
          <textarea
            value={description}
            onChange={(e) => onDescriptionChange?.(e.target.value)}
            className="style-description-input bg-transparent border-none outline-none w-full h-full text-left resize-none"
            style={{ 
              ...typography.medium.regular,
              color: colors.text.primary,
              caretColor: colors.accent
            }}
            placeholder="indie, electronic, synths, 120bpm, distorted"
          />
          </div>
        )}

        {/* Genre Tags */}
        {!isCollapsed && (
          <div className="flex items-center gap-2 p-4 overflow-x-auto scrollbar-hide">
          {hasSavedDescriptions && (
            <MediumButton 
              className="flex-shrink-0"
              onClick={() => setShowSavedModal(true)}
              tooltip="Saved descriptions"
              leftIcon={<BookmarkOutlineIcon isStarred={true} />}
            />
          )}
          
          {genres.map((genre, index) => (
            <div key={index} className="flex-shrink-0">
              <GenreTag 
                genre={genre} 
                onRemove={() => onGenreRemove?.(genre)}
              />
            </div>
          ))}
          
          {suggestionGenres.map((genre, index) => (
            <div key={`suggestion-${index}`} className="flex-shrink-0">
              <GenreTag 
                genre={genre} 
                isAddButton={true}
                isLoading={isLoadingSuggestions}
                onClick={() => handleGenreClick(genre)}
              />
            </div>
          ))}
          </div>
        )}
      </div>

      {/* Drag Handle */}
      {!isCollapsed && (isHovered || isDragging) && (
        <div
          className="absolute left-1/2 transform -translate-x-1/2 cursor-ns-resize"
          style={{
            bottom: '-0px',
            width: '48px',
            height: '16px',
            paddingTop: '8px'
          }}
          onMouseDown={handleDragStart}
          onTouchStart={handleTouchStart}
        >
          <div
            className="w-12 h-1 rounded-full mx-auto"
            style={{
              backgroundColor: 'rgba(255, 255, 255, 0.3)',
              boxShadow: '0px 4px 12px 0 rgba(0,0,0,0.15)'
            }}
          />
        </div>
      )}
    </div>

    {/* Modals */}
    {showSaveModal && (
      <SaveDescriptionModal 
        onSave={handleSaveDescription}
        onClose={() => setShowSaveModal(false)}
      />
    )}
    
    {showSavedModal && (
      <SavedDescriptionsModal 
        savedDescriptions={savedDescriptions}
        onSelect={handleSelectSavedDescription}
        onClose={() => setShowSavedModal(false)}
      />
    )}
    </>
  );
};

export default StyleDescription;